import React, { useState, useEffect } from 'react';
import { FirebaseUser } from '../types/user';
import SpecialityManagementModal from './SpecialityManagementModal';
import SpecialityAdditionModal from './SpecialityAdditionModal';
import { 
  deleteAllUserDataAuthenticated, 
  sendPasswordResetEmailAuthenticated 
} from '../services/adminService';
import './stylesheets/SelectedUserManagementModal.css';
import { formatUserDate } from '../utils/user-directory';
import { specialtyName } from '../data/specialties';
import {updateAdminUser} from '../services/adminService';
import UserConsentHistory from './UserConsentHistory';

interface UserModalProps {
  user: FirebaseUser;
  onClose: () => void;
  onUserUpdate?: () => void | Promise<void>; // Callback to refresh parent data
}

const UserModal: React.FC<UserModalProps> = ({ user, onClose, onUserUpdate }) => {
  const [editingSpecialty, setEditingSpecialty] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(user.name || '');
  const [isUpdatingName, setIsUpdatingName] = useState(false);

  // Disable background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleEditSpecialty = (specialty: string) => {
    setEditingSpecialty(specialty);
  };

  const handleAddSpecialty = () => {
    setShowAddModal(true);
  };

  const handleCloseEditModal = () => {
    setEditingSpecialty(null);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleSpecialtyUpdate = async () => {
    await onUserUpdate?.();
    setEditingSpecialty(null);
    setShowAddModal(false);
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (editedName.trim() === user.name) {
      setIsEditingName(false);
      return;
    }

    setIsUpdatingName(true);
    try {
      await updateAdminUser({uid:user.uid,operation:'rename',name:editedName.trim()});
      setIsEditingName(false);
      if (onUserUpdate) {
        onUserUpdate();
      }
    } catch (error) {
      alert('Failed to update name. Please try again.');
    } finally {
      setIsUpdatingName(false);
    }
  };

  const handleCancelNameEdit = () => {
    setEditedName(user.name || '');
    setIsEditingName(false);
  };

  const handleSendResetPasswordLink = async () => {
    if (window.confirm(`Send password reset link to ${user.email}?`)) {
      try {

        const dataToSend = { email: user.email };
        
        await sendPasswordResetEmailAuthenticated(dataToSend);
        alert(`Password reset link sent successfully to ${user.email}`);
      } catch (error) {
        alert('Failed to send reset link. Please ensure your Cloud Functions are deployed and the user exists in Authentication.');
      }
    }
  };

  const handleDeleteUser = async () => {
    const confirmMessage = `Delete this account and its learning data?\n\nUser: ${user.name || 'N/A'}\nEmail: ${user.email}\n\nPurchase, consent and suppression records may be retained where legally necessary. This cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        
        await deleteAllUserDataAuthenticated(user.uid);

        alert('User deleted successfully from both Firestore and Authentication.');
        onClose();
        if (onUserUpdate) {
          onUserUpdate();
        }
      } catch (error) {
        alert('Deletion did not complete. Please retry or contact support.');
      }
    }
  };
  return (
    <div className="modal-overlay user-management-overlay" onClick={onClose}>
      <div className="modal-container" role="dialog" aria-modal="true" aria-label="Manage user" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">User Management</h2>
            <div className={`user-info-row${window.innerWidth <= 768 ? ' mobile' : ''}`}> 
              <span className="user-email"><strong>Email:</strong> {user.email}</span>
              <span className="user-name"><strong>Name:</strong> {user.name || 'N/A'}</span>
              <span className="user-uid"><strong>UID:</strong> {user.uid}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-close"
            aria-label="Close user management"
          >
            ✕
          </button>
        </div>
        <div className={`modal-body ${window.innerWidth <= 768 ? 'mobile' : ''}`}>
          {/* User Account Management Section */}
          <div className={`section user-account-section${window.innerWidth <= 768 ? ' mobile' : ''}`}>
            <h3 className={`section-title${window.innerWidth <= 768 ? ' mobile' : ''}`}>User Account Management</h3>
            <div className={`form-grid-stacked`}>
              <div className="form-group"><span className="form-label">Account created (UK time)</span><div>{formatUserDate(user.createdAt)}</div></div>
              <div className="form-group"><span className="form-label">Last login (UK time)</span><div>{formatUserDate(user.lastLoginAt)}</div></div>
              {/* Email Field (stacked above name) */}
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  value={user.email}
                  readOnly
                  className="form-input readonly"
                />
              </div>
              {/* Name Field */}
              <div className="form-group">
                <label className="form-label">Display Name</label>
                {isEditingName ? (
                  <div className="edit-controls">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="form-input"
                      autoFocus
                    />
                    <button
                      onClick={handleSaveName}
                      disabled={isUpdatingName}
                      className={`btn-save${isUpdatingName ? ' disabled' : ''}`}
                    >
                      {isUpdatingName ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelNameEdit}
                      disabled={isUpdatingName}
                      className={`btn-cancel${isUpdatingName ? ' disabled' : ''}`}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="view-controls">
                    <span className="form-value">{user.name || 'No name set'}</span>
                    <button
                      onClick={handleEditName}
                      className="btn-edit"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
            <UserConsentHistory uid={user.uid} />
            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                onClick={handleSendResetPasswordLink}
                className="btn-warning"
                title="Send password reset link to user's email"
              >
                Send Reset Password Link
              </button>
              <button
                onClick={handleDeleteUser}
                className="btn-danger"
                title="Permanently delete this user"
              >
                Delete User
              </button>
            </div>
          </div>

          {/* Specialty Management Section */}
          <div className={`section specialty-section ${window.innerWidth <= 768 ? 'mobile' : ''}`}>
            <div className="section-header">
              <h3 className={`section-title ${window.innerWidth <= 768 ? 'mobile' : ''}`}>
                Specialty Management
              </h3>
              <button
                onClick={handleAddSpecialty}
                className={`btn-add ${window.innerWidth <= 768 ? 'mobile' : ''}`}
                title="Add new specialty"
              >
                + Add Specialty
              </button>
            </div>
            {Array.isArray(user.unlockedSpecialties) && user.unlockedSpecialties.length > 0 ? (
              <div className={`specialty-grid ${window.innerWidth <= 768 ? 'mobile' : ''}`}>
                {user.unlockedSpecialties.map((spec: string) => {
                  // Format specialty name
                  const specialty = specialtyName(spec);
                  // Expiry
                  let expiryRaw = undefined;
                  if (user.unlockedSpecialtyExpiries) {
                    if (typeof user.unlockedSpecialtyExpiries.get === 'function') {
                      expiryRaw = user.unlockedSpecialtyExpiries.get(spec);
                    } else {
                      expiryRaw = (user.unlockedSpecialtyExpiries as any)[spec];
                    }
                  }
                  let expiryDisplay = 'N/A';
                  if (expiryRaw) {
                    // Handle ISO string format
                    if (typeof expiryRaw === 'string') {
                      const isoDate = new Date(expiryRaw);
                      if (!isNaN(isoDate.getTime())) {
                        const pad = (n: number) => n.toString().padStart(2, '0');
                        expiryDisplay = `${pad(isoDate.getDate())}/${pad(isoDate.getMonth() + 1)}/${isoDate.getFullYear()} ${pad(isoDate.getHours())}:${pad(isoDate.getMinutes())}:${pad(isoDate.getSeconds())}`;
                      } else {
                        expiryDisplay = expiryRaw;
                      }
                    } else if (typeof expiryRaw === 'object' && typeof expiryRaw.toDate === 'function') {
                      // Handle legacy Timestamp format
                      const date = expiryRaw.toDate();
                      const pad = (n: number) => n.toString().padStart(2, '0');
                      expiryDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
                    } else if (expiryRaw instanceof Date) {
                      const date = expiryRaw;
                      const pad = (n: number) => n.toString().padStart(2, '0');
                      expiryDisplay = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
                    }
                  }
                  // Subscription
                  let subscription = undefined;
                  if (user.specialtySubscriptions) {
                    if (typeof user.specialtySubscriptions.get === 'function') {
                      subscription = user.specialtySubscriptions.get(spec);
                    } else {
                      subscription = (user.specialtySubscriptions as any)[spec];
                    }
                  }
                  // Interview Allowance
                  let allowance = undefined;
                  if (user.interviewAllowance) {
                    if (typeof user.interviewAllowance.get === 'function') {
                      allowance = user.interviewAllowance.get(spec);
                    } else {
                      allowance = (user.interviewAllowance as any)[spec];
                    }
                  }
                  
                  // Calculate total attempts count
                  let totalAttempts = 0;
                  let specialtyAttempts = undefined;
                  
                  // The attempts structure is: attempts.interviewsBySpecialty[spec]
                  if (user.attempts && (user.attempts as any).interviewsBySpecialty) {
                    const interviewsBySpecialty = (user.attempts as any).interviewsBySpecialty;
                    if (typeof interviewsBySpecialty.get === 'function') {
                      specialtyAttempts = interviewsBySpecialty.get(spec);
                    } else {
                      specialtyAttempts = interviewsBySpecialty[spec];
                    }
                  }
                  
                  if (specialtyAttempts && typeof specialtyAttempts === 'object') {
                    if (specialtyAttempts.get && typeof specialtyAttempts.get === 'function') {
                      // It's a Map, convert to object first
                      const attemptsObj: any = {};
                      (specialtyAttempts as any).forEach((value: any, key: any) => {
                        attemptsObj[key] = value;
                        totalAttempts += Number(value) || 0;
                      });
                    } else {
                      // It's a plain object
                      Object.entries(specialtyAttempts).forEach(([k, v]) => {
                        totalAttempts += Number(v) || 0;
                      });
                    }
                  } else if (specialtyAttempts !== undefined) {
                    totalAttempts = Number(specialtyAttempts) || 0;
                  }
                  
                  // Format attempts/allowance display
                  let attemptsDisplay = 'N/A';
                  let remainingDisplay = 'N/A';
                  
                  if (allowance !== undefined && totalAttempts !== undefined) {
                    attemptsDisplay = `${totalAttempts} / ${allowance}`;
                    const remaining = Number(allowance) - totalAttempts;
                    remainingDisplay = remaining >= 0 ? String(remaining) : '0';
                  } else if (totalAttempts > 0) {
                    attemptsDisplay = `${totalAttempts} (no limit set)`;
                    remainingDisplay = 'Unlimited';
                  }
                  return (
                    <div key={spec} className="specialty-card">
                      <div className="card-header">
                        <div className="specialty-name">{specialty}</div>
                        <button
                          onClick={() => handleEditSpecialty(spec)}
                          className="btn-card-edit"
                          title="Edit specialty"
                        >
                          Edit
                        </button>
                      </div>
                      <div><strong>Plan:</strong> {subscription ?? 'N/A'}</div>
                      <div><strong>Attempts:</strong> {attemptsDisplay}</div>
                      <div><strong>Remaining Interviews:</strong> {remainingDisplay}</div>
                      <div><strong>Expiry:</strong> {expiryDisplay}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-specialties">
                <div>No specialties unlocked.</div>
                <button
                  onClick={handleAddSpecialty}
                  className="btn-add-first"
                >
                  + Add First Specialty
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Specialty Edit Modal */}
      {editingSpecialty && (
        <SpecialityManagementModal
          user={user}
          specialty={editingSpecialty}
          onClose={handleCloseEditModal}
          onUpdate={handleSpecialtyUpdate}
        />
      )}
      
      {/* Specialty Addition Modal */}
      {showAddModal && (
        <SpecialityAdditionModal
          user={user}
          onClose={handleCloseAddModal}
          onUpdate={handleSpecialtyUpdate}
        />
      )}
    </div>
  );
};

export default UserModal;
